import type { RevenueAnalyticsModel } from '.';
import {
    applySymbolSearch as applySymbolSearchAction,
    clearSymbolSearch as clearSymbolSearchAction,
    endCsvDownload as endCsvDownloadAction,
    failCsvDownload as failCsvDownloadAction,
    initialRevenueAnalyticsState,
    revenueAnalyticsReducer,
    selectProvider as selectProviderAction,
    setPage as setPageAction,
    setRowsPerPage as setRowsPerPageAction,
    setScope as setScopeAction,
    setSymbolSearchInput as setSymbolSearchInputAction,
    startCsvDownload as startCsvDownloadAction,
    toggleSort as toggleSortAction,
} from './state';
import type {
    RevenueAnalyticsAction,
    RevenueAnalyticsRowsPerPage,
    RevenueAnalyticsSort,
    RevenueAnalyticsSortColumn,
    RevenueAnalyticsState,
} from './state';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type { QueryClient, QueryObserverOptions } from '@tanstack/query-core';
import type { Provider } from '@thinkalpha/ui-types/types/admin/lab';
import { t } from 'i18next';
import { downloadCsvRows } from 'src/admin/LAB/csv/downloadCsv';
import { log } from 'src/admin/LAB/log';
import { buildRevenueAnalyticsCsvRows } from 'src/admin/LAB/RevenueAnalytics/revenueAnalyticsCsv';
import type { RevenueAnalyticsRawRow, RevenueAnalyticsRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { inject, injectable } from 'src/features/ioc';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import { getAllProvidersQuery } from 'src/queries/labProviders';
import { getRevenueAnalyticsQuery, LAB_REVENUE_ANALYTICS } from 'src/queries/labRevenueAnalytics';
import type { ReactBindings } from 'src/types/bindings';

/** Backend page size for CSV export; /v1/orders/summary/by-symbol rejects a `$top` greater than 250. */
const CSV_EXPORT_PAGE_SIZE = 250;
/** Hard cap on exported rows so a huge result set cannot request an unbounded export. */
const CSV_EXPORT_MAX_ROWS = 10_000;

/** OData row-shape field behind each sortable column. */
const SORT_FIELD_BY_COLUMN = {
    provider: 'providerId',
    symbol: 'symbol',
    qty: 'quantity',
    avgPrice: 'averagePrice',
    totalCost: 'totalCost',
    curPrice: 'currentPrice',
} satisfies Record<RevenueAnalyticsSortColumn, string>;

/** Builds the `$orderby` expression for a sort, or undefined for the backend default. */
const sortToOrderby = (sort: RevenueAnalyticsSort | null): string | undefined =>
    sort ? `${SORT_FIELD_BY_COLUMN[sort.column]} ${sort.direction}` : undefined;

@injectable()
export class RevenueAnalyticsModelImpl implements RevenueAnalyticsModel {
    readonly #disposableStack = new DisposableStack();
    readonly #queryClient: QueryClient;
    readonly #state = signal<RevenueAnalyticsState>(initialRevenueAnalyticsState);

    #dispatch(action: RevenueAnalyticsAction): void {
        this.#state.value = revenueAnalyticsReducer(this.#state.value, action);
    }

    readonly #providersQuery: QueryObserverModel<Provider[], Error, Provider[]>;
    readonly #analyticsQuery: QueryObserverModel<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]>;

    readonly providers: ReadonlySignal<ReadonlyArray<Provider>>;
    readonly selectedProviderId: ReadonlySignal<string> = computed(() => this.#state.value.selectedProviderId);
    readonly selectedProvider: ReadonlySignal<Provider | null>;
    readonly rows: ReadonlySignal<ReadonlyArray<RevenueAnalyticsRow>>;
    readonly isLoading: ReadonlySignal<boolean>;
    readonly isError: ReadonlySignal<boolean>;
    readonly isFetching: ReadonlySignal<boolean>;
    readonly lastUpdatedAt: ReadonlySignal<number>;
    readonly isDownloadingCsv: ReadonlySignal<boolean> = computed(() => this.#state.value.isDownloadingCsv);
    readonly csvDownloadFailed: ReadonlySignal<boolean> = computed(() => this.#state.value.csvDownloadFailed);
    readonly symbolSearchInput: ReadonlySignal<string> = computed(() => this.#state.value.symbolSearchInput);
    readonly isSearchActive: ReadonlySignal<boolean> = computed(() => this.#state.value.appliedSymbolSearch !== '');
    readonly page: ReadonlySignal<number> = computed(() => this.#state.value.page);
    readonly rowsPerPage: ReadonlySignal<RevenueAnalyticsRowsPerPage> = computed(() => this.#state.value.rowsPerPage);
    readonly sort: ReadonlySignal<RevenueAnalyticsSort | null> = computed(() => this.#state.value.sort);
    readonly hasPreviousPage: ReadonlySignal<boolean>;
    readonly hasNextPage: ReadonlySignal<boolean>;
    readonly pageStartIndex: ReadonlySignal<number>;
    readonly pageEndIndex: ReadonlySignal<number>;

    constructor(
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('QueryClient') queryClient: QueryClient,
    ) {
        this.#queryClient = queryClient;

        const providersOptions = computed<QueryObserverOptions<Provider[], Error, Provider[]>>(() => {
            const { firmId, superUserMode } = this.#state.value;
            return {
                ...getAllProvidersQuery(firmId),
                // Mirrors `useAllProviders`: superusers can list without a firm.
                enabled: superUserMode || Boolean(firmId),
            };
        });
        this.#providersQuery = queryObserverModelFactory<Provider[], Error, Provider[]>(providersOptions);
        this.#disposableStack.use(this.#providersQuery);

        this.providers = computed(() => this.#providersQuery.signal.value.data ?? []);
        this.selectedProvider = computed(() => {
            const id = this.#state.value.selectedProviderId;
            if (!id) return null;
            return this.providers.value.find((provider) => provider.id === id) ?? null;
        });

        const analyticsOptions = computed<
            QueryObserverOptions<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]>
        >(() => {
            const s = this.#state.value;
            // No selection loads the aggregate across all providers; an id
            // narrows to one. Gate on scope like the providers query so the
            // query doesn't run before a firm/superuser context is known.
            const providerId = s.selectedProviderId || undefined;
            const enabled = s.superUserMode || Boolean(s.firmId);
            // A committed search is answered by the backend `symbol` param;
            // it returns the single match, so paging and sorting are bypassed.
            if (s.appliedSymbolSearch) {
                return { ...getRevenueAnalyticsQuery({ providerId, symbol: s.appliedSymbolSearch }), enabled };
            }
            return {
                ...getRevenueAnalyticsQuery({
                    providerId,
                    orderby: sortToOrderby(s.sort),
                    skip: (s.page - 1) * s.rowsPerPage,
                    top: s.rowsPerPage,
                }),
                enabled,
            };
        });
        this.#analyticsQuery = queryObserverModelFactory<RevenueAnalyticsRawRow[], Error, RevenueAnalyticsRawRow[]>(
            analyticsOptions,
        );
        this.#disposableStack.use(this.#analyticsQuery);

        // Pair each raw row with its provider's display name; the aggregate
        // view spans providers, so names are resolved here rather than baked
        // in at fetch time. Falls back to the id if the name isn't loaded yet.
        this.rows = computed<RevenueAnalyticsRow[]>(() => {
            const raw = this.#analyticsQuery.signal.value.data ?? [];
            const nameById = new Map(
                this.providers.value.map((provider): [string, string] => [provider.id, provider.name]),
            );
            return raw.map((row) => ({ ...row, providerName: nameById.get(row.providerId) ?? row.providerId }));
        });
        this.isLoading = computed(() => this.#analyticsQuery.signal.value.isLoading);
        this.isError = computed(() => this.#analyticsQuery.signal.value.isError);
        this.isFetching = computed(() => this.#analyticsQuery.signal.value.isFetching);
        this.lastUpdatedAt = computed(() => this.#analyticsQuery.signal.value.dataUpdatedAt);

        this.hasPreviousPage = computed(() => !this.isSearchActive.value && this.#state.value.page > 1);
        // No total count from the backend yet (a follow-up will add it): a full
        // page implies there may be more, so Next is enabled only when the
        // current page came back full.
        this.hasNextPage = computed(
            () => !this.isSearchActive.value && this.rows.value.length === this.#state.value.rowsPerPage,
        );
        this.pageStartIndex = computed(() => {
            if (this.rows.value.length === 0) return 0;
            if (this.isSearchActive.value) return 1;
            const s = this.#state.value;
            return (s.page - 1) * s.rowsPerPage + 1;
        });
        this.pageEndIndex = computed(() => {
            const count = this.rows.value.length;
            if (count === 0) return 0;
            if (this.isSearchActive.value) return count;
            const s = this.#state.value;
            return (s.page - 1) * s.rowsPerPage + count;
        });
    }

    setScope(firmId: string | undefined, superUserMode: boolean): void {
        this.#dispatch(setScopeAction(firmId, superUserMode));
    }

    selectProvider(providerId: string): void {
        this.#dispatch(selectProviderAction(providerId));
    }

    setRowsPerPage(rowsPerPage: RevenueAnalyticsRowsPerPage): void {
        this.#dispatch(setRowsPerPageAction(rowsPerPage));
    }

    toggleSort(column: RevenueAnalyticsSortColumn): void {
        this.#dispatch(toggleSortAction(column));
    }

    goToPreviousPage(): void {
        if (this.hasPreviousPage.value) this.#dispatch(setPageAction(this.#state.value.page - 1));
    }

    goToNextPage(): void {
        if (this.hasNextPage.value) this.#dispatch(setPageAction(this.#state.value.page + 1));
    }

    setSymbolSearchInput(value: string): void {
        this.#dispatch(setSymbolSearchInputAction(value));
    }

    applySymbolSearch(): void {
        this.#dispatch(applySymbolSearchAction());
    }

    clearSymbolSearch(): void {
        this.#dispatch(clearSymbolSearchAction());
    }

    async refresh(): Promise<void> {
        const providerId = this.#state.value.selectedProviderId;
        await this.#queryClient.invalidateQueries({ queryKey: [LAB_REVENUE_ANALYTICS, providerId || null] });
    }

    async downloadCsv(): Promise<void> {
        if (typeof document === 'undefined') return;
        // Guard re-entrancy: the button also disables on this flag, but a
        // keyboard-repeat or programmatic caller could still fire twice.
        if (this.#state.value.isDownloadingCsv) return;
        this.#dispatch(startCsvDownloadAction());
        try {
            const s = this.#state.value;
            const providerId = s.selectedProviderId || undefined;
            const raw: RevenueAnalyticsRawRow[] = [];
            if (s.appliedSymbolSearch) {
                // A committed search returns the single match; export just it.
                raw.push(...(await this.#fetchAnalytics({ providerId, symbol: s.appliedSymbolSearch })));
            } else {
                // The user expects the full result set, not just the current
                // page. Walk the backend pages (no total count is reported)
                // until a short page, capped so an unexpectedly huge result
                // set cannot request an unbounded export. The active sort is
                // carried so the file reads in the on-screen order.
                const orderby = sortToOrderby(s.sort);
                let skip = 0;
                for (;;) {
                    const page = await this.#fetchAnalytics({ providerId, orderby, skip, top: CSV_EXPORT_PAGE_SIZE });
                    raw.push(...page);
                    if (page.length < CSV_EXPORT_PAGE_SIZE || raw.length >= CSV_EXPORT_MAX_ROWS) break;
                    skip += CSV_EXPORT_PAGE_SIZE;
                }
            }
            if (raw.length === 0) return;
            const nameById = new Map(
                this.providers.value.map((provider): [string, string] => [provider.id, provider.name]),
            );
            const rows = raw
                .slice(0, CSV_EXPORT_MAX_ROWS)
                .map((row) => ({ ...row, providerName: nameById.get(row.providerId) ?? row.providerId }));
            downloadCsvRows(
                'revenue-analytics',
                buildRevenueAnalyticsCsvRows(rows, {
                    provider: t('admin::lab::revenue-analytics::col-provider', 'Provider'),
                    symbol: t('admin::lab::revenue-analytics::col-symbol', 'Symbol'),
                    qty: t('admin::lab::revenue-analytics::col-qty', 'Qty'),
                    avgPrice: t('admin::lab::revenue-analytics::col-avg-price', 'Avg Price'),
                    totalCost: t('admin::lab::revenue-analytics::col-total-cost', 'Total Cost'),
                    curPrice: t('admin::lab::revenue-analytics::col-cur-price', 'Cur Price'),
                    fairValue: t('admin::lab::revenue-analytics::col-fair-value', 'Fair Value'),
                    sodInventory: t('admin::lab::revenue-analytics::col-sod-inv', 'SOD Inv'),
                    remainingInventory: t('admin::lab::revenue-analytics::col-rem-inv', 'Rem Inv'),
                    percentUsed: t('admin::lab::revenue-analytics::col-pct-used', '% Used'),
                    percentRemaining: t('admin::lab::revenue-analytics::col-pct-rem', '% Rem'),
                }),
            );
        } catch (error) {
            log.error('Revenue Analytics CSV export failed', { error });
            this.#dispatch(failCsvDownloadAction());
        } finally {
            this.#dispatch(endCsvDownloadAction());
        }
    }

    #fetchAnalytics(options: Parameters<typeof getRevenueAnalyticsQuery>[0]): Promise<RevenueAnalyticsRawRow[]> {
        const queryOptions = getRevenueAnalyticsQuery(options);
        return this.#queryClient.fetchQuery({ ...queryOptions, queryKey: queryOptions.queryKey ?? [] });
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
